import React, { useMemo, useCallback } from 'react';
import { TimeSeriesTrendsResult } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { TimeSeriesTrendsRequest } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { constructTimeSeriesTrendsExplanationCopy } from 'libs/end-to-end/src/time-series-trends-explanation-utils';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DownloadButton } from 'apps/sigma2-ui/src/components/ui/DownloadButton';
import { IconDownloadAnalysisSvg } from 'apps/sigma2-ui/src/components/svg/IconDownloadAnalysis';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { getTimeSeriesTrendsExplanationAttributes } from 'libs/end-to-end/src/time-series-trends-explanation-utils';

export type TimeTrendsExplanationProps = {
  data: TimeSeriesTrendsResult | null;
  request: TimeSeriesTrendsRequest;
  myObservation: MyObservation | null;
  downloadContainerRef?: React.RefObject<HTMLDivElement> | null;
};

export const TimeTrendsExplanation = ({
  data,
  request,
  myObservation,
  downloadContainerRef = null,
}: TimeTrendsExplanationProps) => {
  const copy = useMemo(
    () =>
      constructTimeSeriesTrendsExplanationCopy({
        data: data ?? undefined,
        request,
        myObservation,
      }),
    [data, request, myObservation],
  );

  const language = useLanguage();
  const analytics = useAnalytics();

  const getElementToDownload = useCallback(
    () => downloadContainerRef?.current ?? null,
    [downloadContainerRef],
  );

  const filename = useMemo(() => {
    const { metricDisplay, dimensionDisplay } =
      getTimeSeriesTrendsExplanationAttributes({
        request,
        myObservation,
      });
    return `TimeTrends-${metricDisplay}-${dimensionDisplay}-${getDateAsYYYYMMDD(new Date())}`;
  }, [request, myObservation]);

  const onDownloadCb = useCallback(() => {
    analytics.trackEvent({
      eventName: 'Chart Downloaded',
      eventProperties: { type: 'Group Trends' },
    });
  }, [analytics]);

  return (
    <Box as="div">
      <p className="text-xs leading-6 text-gray-900">{copy}</p>

      {downloadContainerRef && (
        <div className="py-3 text-xs leading-6 font-bold text-royal-800">
          <DownloadButton
            getElement={getElementToDownload}
            filename={filename}
            onDownloadCb={onDownloadCb}
          >
            <Spacing direction="horizontal" size="xs" className="items-center">
              <IconDownloadAnalysisSvg />
              <span>
                {convertTranslatedContentToReactNode(
                  language.downloadAnalysis(),
                )}
              </span>
            </Spacing>
          </DownloadButton>
        </div>
      )}
    </Box>
  );
};
